<template>
  <div class="vehicle-variants">
    <div class="vehicle-variants__title row items-center justify-between q-mb-md">
      <h3 class="vehicle-variants__heading">Variants | {{ title }}</h3>
      <QBtn flat dense round :icon="evaCloseOutline" @click="$emit('close')" aria-label="Close variants" />
    </div>
    <div class="vehicle-variants__viewport">
      <div class="vehicle-variants__track" :style="trackStyle">
        <div v-for="variant in variants" :key="variant.id" class="vehicle-variants__slide">
          <VehicleShortlistCard
            class="view-shortlist-result-card"
            :imagePath="variantImageUrl(variant)"
            :imagePathTrim="variantImageUrl(variant)"
            :imagePathBase="variantImageUrl(variant)"
            :fuelType="variantMeta(variant)"
            :vehicleName="variantName(variant)"
            :vehicleDescription="variantDescription(variant)"
            :vehicleMake="variant.vehicleMake"
            :weeklyPrice="variant.weekLeaseprice"
            :monthlyPrice="variant.monthLeaseprice"
            :rrp="variant.consumerPrice"
            :repaymentOption="repayment"
            :showPaymentBreakdown="false"
            :showRemoveBtn="false"
            :showViewVehicle="true"
            trim-image-contain
            :isShortlisted="isVariantShortlisted(variant)"
            :shortlistAtMax="isShortlistAtMax"
            @select="$emit('select-variant', variant)"
            @onViewVehicle="$emit('on-view-vehicle', variant)"
            @onShortlist="$emit('on-shortlist', variant)"
          />
        </div>
      </div>
    </div>
    <div v-if="showVariantNav" class="row justify-center items-center q-gutter-md vehicle-variants__nav">
      <QBtn
        flat
        :color="canGoBack ? 'primary' : 'grey-5'"
        round
        dense
        :disable="!canGoBack"
        :icon="arrowIosBackOutline"
        aria-label="Previous variants"
        @click="carouselBack"
      />
      <QBtn
        flat
        :color="canGoForward ? 'primary' : 'grey-5'"
        round
        dense
        :disable="!canGoForward"
        :icon="arrowIosForwardOutline"
        aria-label="Next variants"
        @click="carouselForward"
      />
    </div>
  </div>
</template>

<script>
import { QBtn } from 'quasar';
import { evaCloseOutline, evaArrowIosForwardOutline, evaArrowIosBackOutline } from '@quasar/extras/eva-icons';
import { VP_MOBILE_MAX, VP_TABLET_MAX } from '../../../styles/calculator/vehicle-product-page-breakpoints';
import VehicleShortlistCard from '../ViewShortlist/VehicleShortlistCard.vue';
import { formatVehicleDescription } from '../../../utils/calculator/vehicleFormatter';

export default {
  name: 'VehicleVariants',
  components: {
    QBtn,
    VehicleShortlistCard
  },
  props: {
    /** Array of ratebook items (one per catalog vehicle) */
    variants: {
      type: Array,
      default: () => []
    },
    /** Current shortlist (for showing Shortlisted state per variant) */
    shortList: {
      type: Array,
      default: () => []
    },
    repayment: {
      type: String,
      default: 'weekly'
    },
    title: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      variantCarouselIndex: 0,
      slidesVisible: this.getSlidesVisible()
    };
  },
  computed: {
    isShortlistAtMax() {
      const MAX_SHORTLIST_COUNT = 3;
      return this.shortList.length >= MAX_SHORTLIST_COUNT;
    },
    showVariantNav() {
      return this.variants.length > this.slidesVisible;
    },
    canGoBack() {
      return this.variantCarouselIndex > 0;
    },
    canGoForward() {
      return this.variants.length > this.slidesVisible && this.variantCarouselIndex < this.variants.length - this.slidesVisible;
    },
    trackStyle() {
      const n = this.variants.length;
      const slides = this.slidesVisible;
      // Track width so that slidesVisible slides fit in the viewport
      const widthPercent = (n * 100) / slides;
      // translateX % is relative to the TRACK; one slide = (100/n)% of track
      const translateXPercent = -(this.variantCarouselIndex * (100 / n));
      return {
        '--variants-count': n,
        'width': `${widthPercent}%`,
        'transform': `translateX(${translateXPercent}%)`
      };
    }
  },
  mounted() {
    this.updateSlidesVisible();
    window.addEventListener('resize', this.updateSlidesVisible);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateSlidesVisible);
  },
  methods: {
    getSlidesVisible() {
      if (typeof window === 'undefined') return 3;
      const w = window.innerWidth;
      if (w <= VP_MOBILE_MAX) return 1; // mobile: 1
      if (w <= VP_TABLET_MAX) return 2; // tablet: 2
      return 3; // desktop: 3
    },
    updateSlidesVisible() {
      this.slidesVisible = this.getSlidesVisible();
    },
    carouselBack() {
      if (this.canGoBack) {
        this.variantCarouselIndex = Math.max(0, this.variantCarouselIndex - 1);
      }
    },
    carouselForward() {
      if (this.canGoForward) {
        this.variantCarouselIndex = Math.min(this.variants.length - this.slidesVisible, this.variantCarouselIndex + 1);
      }
    },
    variantImageUrl(item) {
      return item?.jatoImagepathTrim || item?.jatoImagepath || '';
    },
    variantMeta(item) {
      if (!item) return '';
      return [item.catalogYear, item.vehicleFueltype, item.vehicleTransmissiongroup].filter(Boolean).join(' | ');
    },
    variantName(item) {
      if (!item) return '';
      return `${item.vehicleMake} ${item.vehicleModel} ${item.vehicleTrim}`;
    },
    variantDescription(item) {
      return formatVehicleDescription(item)?.trim() || '';
    },
    isVariantShortlisted(variant) {
      return this.shortList.some((item) => item.catalogVehicleId === variant?.catalogVehicleId);
    }
  },
  created() {
    this.evaCloseOutline = evaCloseOutline;
    this.arrowIosForwardOutline = evaArrowIosForwardOutline;
    this.arrowIosBackOutline = evaArrowIosBackOutline;
  }
};
</script>
